import React, { useEffect } from 'react';
import { connect as connectR } from 'react-redux';
import { Link } from 'react-router';
import { Button, Col, Row } from 'react-bootstrap';
import _ from 'lodash';
import LeftSide from '../main/left-side';
import ObjectStatisticsCard from './object-statistics';
import LargeCard from '../main/large-card';
import VerticalNavigation from '../navigation/vertical-navigation';
import LinkButton from '../util/link-button';
import {
    LoadLocationsForMansionId,
    MansionById,
} from '../../store/locationsSlice';
import {
    REQUEST_INITIAL,
    REQUEST_PENDING,
} from '../../store/network/managedRequest';
import { LoadMansions, Mansion } from '../../store/mansionsSlice';
import {
    MansionStatsDto,
    MaybeLoadObjectStats,
} from '../../store/objectStatsSlice';
import useTranslate from '../useTranslate';
import { RootState } from '../../store';
import useFeatureAccess from '../../util/useFeatureAccess';
import { ManagedPartnerDto } from '../../store/partnerManagerSlice';
import ObjectOperationalContactsCard from './object-operational-contacts-card';
import { PbRequest } from '../../store/pbRequest';
import { PbGenericRequest } from '../../store/pbRequestSlice';
import { OperationalContactsListDto } from '../../store/operationalContactsSlice';
import Postit from '../main/postit';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons/faExclamationTriangle';
import { useParams } from 'react-router-dom';

interface Props {
    getLocationsByMansion: PbGenericRequest;
    locationsByMansionsById: MansionById;
    mansionsById: { [key: string]: Mansion };
    mansionIdToStats: { [mansionId: number]: MansionStatsDto };
    loadMansionsRequest: PbGenericRequest;
    selectedPartner: ManagedPartnerDto;
    operationalContacts: OperationalContactsListDto | undefined;

    LoadMansions: () => void;
    LoadLocationsForMansionId: (mansionId: number) => void;
    MaybeLoadObjectStats: (mansionId: number) => void;
}

function ObjectsDetails(props: Readonly<Props>) {
    const params = useParams();
    const objectId = Number(params.object_id);
    const strings = useTranslate('ObjectsDetails');
    const operationalContactsRef = React.useRef<HTMLDivElement>(null);
    const { canReadFeature, canWriteFeature } = useFeatureAccess(
        props.selectedPartner
    );
    const hasWriteAccess = canWriteFeature('RESIDENTIAL_MANSION');
    const hasWriteAppliances = canWriteFeature('RESIDENTIAL_APPLIANCE');
    const hasReadLocations = canReadFeature('RESIDENTIAL_LOCATION');

    const hasMissingContacts =
        props.operationalContacts?.mansions_with_missing_contact.includes(
            objectId
        ) ?? false;

    useEffect(() => {
        props.MaybeLoadObjectStats(Number(params.object_id));

        if (props.loadMansionsRequest.status === REQUEST_INITIAL) {
            props.LoadMansions();
        }

        props.LoadLocationsForMansionId(Number(params.object_id));
    }, []);

    useEffect(() => {
        Object.values(props.mansionsById).forEach((object) =>
            props.LoadLocationsForMansionId(object.id)
        );
    }, [props.mansionsById]);

    const locationsLoading = [REQUEST_INITIAL, REQUEST_PENDING].includes(
        props.getLocationsByMansion.status
    );
    const mansionsLoading = [REQUEST_INITIAL, REQUEST_PENDING].includes(
        props.loadMansionsRequest.status
    );

    if (mansionsLoading || locationsLoading) {
        return <div> Loading </div>;
    }
    const object = props.mansionsById[Number(params.object_id)];
    const locations = props.locationsByMansionsById;
    const showOperationalContactWarning = hasMissingContacts && hasWriteAccess;

    if (object) {
        return (
            <Row>
                <LeftSide addClass="">
                    <VerticalNavigation
                        selected="objects"
                        selectedObject={object.id}
                        objects={props.mansionsById}
                        locations={locations}
                    />
                </LeftSide>

                <Col xs={6} className="col-middle">
                    {showOperationalContactWarning && (
                        <Postit>
                            <FontAwesomeIcon
                                className={'mr-2'}
                                icon={faExclamationTriangle}
                                size={'lg'}
                            />
                            {strings.warnOperationalContactsText}
                            <Button
                                bsStyle={'link'}
                                onClick={handleScrollToOperationalContacts}
                            >
                                {strings.warnOperationalContactsButton}
                            </Button>
                        </Postit>
                    )}
                    <ObjectStatisticsCard
                        stats={props.mansionIdToStats[object.id]}
                    />
                    <LargeCard
                        type="card-edit"
                        title={[
                            object.address.street +
                                ' ' +
                                object.address.housenumber,
                            strings.locations,
                        ]}
                    >
                        {hasWriteAccess && (
                            <LinkButton
                                text={strings.editObject}
                                to={'/objects/' + object.id + '/overview'}
                                pullRight
                                marginTop="0.0em"
                                data-testid="edit-object-button"
                            />
                        )}

                        <table className="data-table" style={{ width: '100%' }}>
                            <thead>
                                <tr>
                                    <th>{strings.location}</th>
                                    <th>{strings.numberOfAppliances}</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {_.map(locations[object.id], (location) => {
                                    return (
                                        <tr key={location.id}>
                                            <td>{location.name}</td>
                                            <td>
                                                {location.no_of_appliances ?? 0}
                                            </td>
                                            <td>
                                                {hasReadLocations && (
                                                    <Link
                                                        to={
                                                            '/objects/' +
                                                            object.id +
                                                            '/' +
                                                            location.id
                                                        }
                                                        className="icon icon-wew-button-enter icon-sm"
                                                    />
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {hasWriteAppliances && (
                            <LinkButton
                                text={strings.addLocation}
                                to={
                                    '/objects/' + object.id + '/create-location'
                                }
                                pullRight
                                marginTop="0.5em"
                                data-testid="add-location-button"
                            />
                        )}
                    </LargeCard>
                    {!!object.id && (
                        <div ref={operationalContactsRef}>
                            <ObjectOperationalContactsCard
                                mansionId={object.id}
                            />
                        </div>
                    )}
                </Col>
            </Row>
        );
    } else {
        return <div>Loading!</div>;
    }

    function handleScrollToOperationalContacts() {
        operationalContactsRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
}

export default connectR(
    (store: RootState) => ({
        getLocationsByMansion:
            store.pbRequest[PbRequest.GET_LOCATIONS_BY_MANSION],
        locationsByMansionsById: store.locations.locationsByMansionsById,
        loadMansionsRequest: store.pbRequest[PbRequest.LOAD_MANSIONS],
        mansionsById: store.mansions.mansionsById,
        mansionIdToStats: store.objectStats.mansionIdToStats,
        selectedPartner: store.partnerManager.selectedPartner,
        operationalContacts: store.operationalContacts.operationalContacts,
    }),
    { LoadLocationsForMansionId, LoadMansions, MaybeLoadObjectStats }
)(ObjectsDetails);
